import React, { useEffect, useState } from "react";
import { FiDownload } from "react-icons/fi";
import { toast } from "react-toastify";
import DataTable from "./DataTable";
import StatusPill from "./StatusPill";
import { kpisLiquidacao, projecaoLiquidacao, totaisProjecao, cenariosLiquidacao } from "../mockData";
import validacaoFiscalService from "../../../services/validacaoFiscalService";

function formatCurrency(num) {
  return Number(num || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function LiquidacaoTab({ competenciaId }) {
  const [cenarioSelecionado, setCenarioSelecionado] = useState("B");
  const [real, setReal] = useState(null);
  const [loading, setLoading] = useState(!!competenciaId);
  const [projetando, setProjetando] = useState(false);
  const [registrando, setRegistrando] = useState(false);

  const carregar = () => {
    if (!competenciaId) return;
    setLoading(true);
    validacaoFiscalService
      .obterLiquidacao(competenciaId)
      .then((resp) => setReal(resp?.data?.data || null))
      .catch((err) => console.error("[LiquidacaoTab] Erro:", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (competenciaId) carregar();
    else setReal(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competenciaId]);

  const usandoReal = !!competenciaId;
  const projecao = real?.projecao;
  const itensReal = (projecao?.itens || []).map((i) => ({
    chave: i.chaveAcesso || i.documentoId,
    autorizacao: i.autorizadoEm ? new Date(i.autorizadoEm).toLocaleString("pt-BR") : "—",
    debito: i.debito,
    creditoImputado: i.creditoImputado,
    saldoAberto: i.saldoAberto,
  }));
  const linhas = usandoReal ? itensReal : projecaoLiquidacao;

  const totais = usandoReal
    ? linhas.reduce(
        (acc, i) => ({
          debito: acc.debito + i.debito,
          creditoImputado: acc.creditoImputado + i.creditoImputado,
          saldoAberto: acc.saldoAberto + i.saldoAberto,
        }),
        { debito: 0, creditoImputado: 0, saldoAberto: 0 }
      )
    : totaisProjecao;

  const handleGerarProjecao = async () => {
    setProjetando(true);
    try {
      await validacaoFiscalService.projetarLiquidacao(competenciaId);
      toast.success("Projeção gerada.");
      carregar();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Erro ao gerar projeção.");
    } finally {
      setProjetando(false);
    }
  };

  const handleRegistrarDecisao = async () => {
    if (!usandoReal) return;
    setRegistrando(true);
    try {
      let projecaoId = projecao?.id;
      if (!projecaoId) {
        const resp = await validacaoFiscalService.projetarLiquidacao(competenciaId);
        projecaoId = resp?.data?.data?.id;
      }
      await validacaoFiscalService.registrarDecisaoLiquidacao(competenciaId, {
        projecaoId,
        cenario: cenarioSelecionado,
        justificativa: "Decisão registrada pela tela de Liquidação Planejada.",
      });
      toast.success("Decisão do cenário registrada.");
      carregar();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Erro ao registrar decisão.");
    } finally {
      setRegistrando(false);
    }
  };

  const columns = [
    { key: "chave", label: "Documento (chave)" },
    { key: "autorizacao", label: "Autorização" },
    { key: "debito", label: "Débito CBS (R$)", align: "right", render: (r) => formatCurrency(r.debito) },
    {
      key: "creditoImputado",
      label: "Crédito imputado (R$)",
      align: "right",
      render: (r) => formatCurrency(r.creditoImputado),
    },
    { key: "saldoAberto", label: "Saldo aberto (R$)", align: "right", render: (r) => formatCurrency(r.saldoAberto) },
    {
      key: "split",
      label: "Split",
      render: (r) =>
        r.saldoAberto > 0 ? (
          <StatusPill variant="warning">Split sobre R$ {formatCurrency(r.saldoAberto)}</StatusPill>
        ) : (
          <StatusPill variant="success">Sem split</StatusPill>
        ),
    },
  ];

  const footer = {
    chave: <strong>Total</strong>,
    autorizacao: "",
    debito: <strong>{formatCurrency(totais.debito)}</strong>,
    creditoImputado: <strong>{formatCurrency(totais.creditoImputado)}</strong>,
    saldoAberto: <strong>{formatCurrency(totais.saldoAberto)}</strong>,
    split: "",
  };

  const kpis = usandoReal
    ? [
        { label: "Créditos disponíveis (apropriados)", valor: `R$ ${formatCurrency(real?.kpis?.creditosDisponiveis)}`, caption: "podem ser usados · art. 53" },
        { label: "Créditos a apropriar", valor: `R$ ${formatCurrency(real?.kpis?.creditosAApropriar)}`, caption: "débito do fornecedor ainda não extinto · art. 47" },
        { label: "Vencem em até 12 meses", valor: `R$ ${formatCurrency(real?.kpis?.venceEm12Meses)}`, caption: "prazo de 5 anos · art. 54" },
        { label: "Exposição ao split", valor: `R$ ${formatCurrency(real?.kpis?.exposicaoSplit)}`, caption: "da última projeção gerada" },
      ]
    : kpisLiquidacao;

  const cenarios = cenariosLiquidacao.map((c) =>
    c.id === "A" && usandoReal ? { ...c, retido: `R$ ${formatCurrency(real?.kpis?.exposicaoSplit)}` } : c
  );

  return (
    <>
      <div className="section-title">
        <h4 style={{ fontSize: "1.1em", opacity: 1 }}>Liquidação planejada {loading && "(atualizando…)"}</h4>
      </div>
      <p className="table-footnote" style={{ maxWidth: "80ch", marginBottom: 16 }}>
        Projeção de como os créditos disponíveis serão imputados aos débitos e de quais operações ainda
        ficarão sujeitas ao split payment. É uma projeção para planejar o caixa; a imputação oficial é
        feita pela RFB.
      </p>

      <div className="info-banner">
        <div className="banner-text">
          <strong>Ordem legal. </strong>
          <span>
            A compensação segue a ordem cronológica de autorização dos documentos (Decreto 12.955/2026,
            art. 26, § 1º, I; art. 53). O cliente não escolhe o documento a extinguir.
          </span>
        </div>
      </div>

      <div className="kpi-cards">
        {kpis.map((kpi) => (
          <div className="kpi-card" key={kpi.label}>
            <div className="kpi-label">{kpi.label}</div>
            <div className="kpi-value">{kpi.valor}</div>
            <div className="kpi-caption">{kpi.caption}</div>
          </div>
        ))}
      </div>

      {usandoReal && !projecao && (
        <div className="info-banner warning" style={{ marginBottom: 16 }}>
          <div className="banner-text">
            <strong>Nenhuma projeção gerada ainda.</strong>
            <span> Clique em "Gerar projeção" para simular a imputação dos créditos disponíveis.</span>
          </div>
          <button type="button" className="icon-btn primary" onClick={handleGerarProjecao} disabled={projetando}>
            {projetando ? "Gerando…" : "Gerar projeção"}
          </button>
        </div>
      )}

      <DataTable
        title="Projeção da extinção · débitos abertos do período"
        subtitle="Ordem por momento de autorização · janela de ordenação de 48 h (parâmetro)"
        columns={columns}
        rows={linhas}
        footer={footer}
        minWidth={820}
      />

      <div className="section-title">
        <h4 style={{ fontSize: "0.95em" }}>Cenários para o saldo aberto de R$ {formatCurrency(totais.saldoAberto)}</h4>
      </div>
      <div className="scenario-cards">
        {cenarios.map((c) => (
          <div
            key={c.id}
            className={`scenario-card ${c.id === cenarioSelecionado ? "referencia" : ""}`}
            onClick={() => setCenarioSelecionado(c.id)}
            style={{ cursor: "pointer" }}
          >
            {c.id === cenarioSelecionado && <div className="scenario-ref-tag">Selecionado</div>}
            <div className="scenario-title">{c.titulo}</div>
            <div className="scenario-desc">{c.descricao}</div>
            <div className="scenario-footer">
              <span>Retido no split</span>
              <strong>{c.retido}</strong>
            </div>
          </div>
        ))}
      </div>

      <div className="header-actions" style={{ marginBottom: 20, alignItems: "center" }}>
        <button type="button" className="icon-btn primary" onClick={handleRegistrarDecisao} disabled={!usandoReal || registrando}>
          {registrando ? "Registrando…" : "Registrar decisão do cenário"}
        </button>
        <button type="button" className="icon-btn" disabled title="Disponível em breve">
          <FiDownload /> Exportar projeção (PDF)
        </button>
        <span className="table-footnote" style={{ alignSelf: "center" }}>
          Execução pela RFB: a confirmar. Nesta fase a ferramenta recomenda e o cliente executa no portal.
        </span>
      </div>

      <div className="table-footnote">
        {usandoReal
          ? "Projeção calculada a partir dos créditos apropriados e débitos abertos reais desta competência."
          : "Dados ilustrativos. A projeção pode divergir do processamento oficial; a RFB é a fonte da imputação."}
      </div>
    </>
  );
}
